import React from "react";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

const css = `
:root{
  --ps-bg:
    radial-gradient(1200px 600px at 80% 20%, rgba(129,161,255,.22), transparent 60%),
    radial-gradient(900px 500px at 10% 80%, rgba(255,170,210,.18), transparent 60%),
    linear-gradient(180deg, #ffffff, #fbfbff 40%, #ffffff 100%);
  --ps-black:#0b0b0c;
  --ps-muted:#6b7280;
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-line:#eaeaef;
  --ps-card:#fff;
  --nav-h: 56px;
  --nav-border: rgba(17, 24, 39, .08);
}

/* Layout + scrolling (reuse from Home) */
.page-scroll {
  height: calc(100vh - var(--nav-h, 56px));
  overflow: auto;
  scroll-behavior: smooth;
}
@media (max-width: 640px) {
  .page-scroll {
    height: calc(100svh - var(--nav-h, 56px));
  }
}
.page-scroll {
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
}
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track {
  background:#eef1ff;
  border-left:1px solid var(--nav-border, rgba(17,24,39,.08));
}
.page-scroll::-webkit-scrollbar-thumb {
  background:linear-gradient(180deg,#c7d2fe,#a78bfa);
  border:2px solid #eef1ff;
  border-radius:8px;
}

html, body { height: 100%; overflow-y: hidden !important; }
body { overflow-y: hidden !important; }

.ps-wrap{ background:var(--ps-bg); min-height:100svh; }
@supports (min-height: 100dvh){
  .ps-wrap{ min-height:100dvh; }
}
.ps-container{
  max-width:900px;
  margin:0 auto;
  padding:56px 24px 40px;
}

/* Legal page */
.legal-page {
  background: rgba(255,255,255,0.9);
  border-radius: 24px;
  border: 1px solid rgba(148,163,184,0.4);
  padding: 28px 24px 26px;
  box-shadow: 0 18px 45px rgba(15,23,42,0.08);
}
@media (min-width: 900px){
  .legal-page {
    padding: 32px 32px 30px;
  }
}
.legal-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 18px;
  margin-bottom: 18px;
}
.legal-eyebrow {
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-size:11px;
  font-weight:700;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:#6b7280;
}
.legal-dot {
  width:6px; height:6px; border-radius:999px;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
}
.legal-title {
  margin:10px 0 4px;
  font-size:28px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:var(--ps-black);
}
.legal-meta {
  font-size:13px;
  color:#9ca3af;
}
.legal-meta strong {
  font-weight:600;
  color:#6b7280;
}

.legal-content {
  font-size:14px;
  color:#374151;
  line-height:1.7;
}
.legal-content p {
  margin:0 0 12px;
}
.legal-section {
  margin-top:22px;
}
.legal-section h2 {
  font-size:18px;
  margin:0 0 6px;
  color:var(--ps-black);
}
.legal-section h3 {
  font-size:15px;
  margin:14px 0 4px;
  color:var(--ps-black);
}
.legal-section ul {
  margin:0 0 12px 1.1rem;
  padding:0;
}
.legal-section li {
  margin-bottom:4px;
}
.legal-small {
  font-size:12px;
  color:#9ca3af;
  margin-top:18px;
}

/* Footer (simple) */
.legal-footer {
  margin-top:20px;
  padding-top:16px;
  border-top:1px solid rgba(148,163,184,0.4);
  display:flex;
  flex-wrap:wrap;
  gap:10px;
  justify-content:space-between;
  align-items:center;
  font-size:12px;
  color:#9ca3af;
}
.legal-footer a {
  color:#6b7280;
  text-decoration:none;
}
.legal-footer a:hover {
  color:var(--ps-black);
  text-decoration:underline;
}

@media (max-width: 640px){
  .ps-container{
    padding-inline:16px;
  }
  .legal-page{
    border-radius:20px;
    padding-inline:18px;
  }
}
`;

export default function Privacy() {
  const seo = seoPresets["/privacy"];
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
      />
    <div className="ps-wrap">
      <style>{css}</style>
      <div className="page-scroll">
        <main className="ps-container">
          <article className="legal-page">
            <header className="legal-header">
              <div className="legal-eyebrow">
                <span className="legal-dot" aria-hidden="true" />
                <span>Legal</span>
              </div>
              <h1 className="legal-title">Privacy Policy</h1>
              <p className="legal-meta">
                <strong>Last updated:</strong> 24 November 2025
              </p>
            </header>

            <section className="legal-content">
              <p>
                MusicBase (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a playlist outreach tool that helps
                artists, labels, and managers find playlist curators and run email campaigns from their own inbox.
              </p>
              <p>
                This Privacy Policy explains how we collect, use, and share information when you use the MusicBase web
                application, website, and related services (collectively, the &quot;Service&quot;).
              </p>
              <p>By using MusicBase, you agree to the practices described here.</p>

              <div className="legal-section" id="who-we-are">
                <h2>1. Who we are &amp; how to contact us</h2>
                <p>
                  <strong>MusicBase</strong>
                  <br />
                  Email:{" "}
                  <a href="mailto:privacy@musicbase.io">
                    privacy@musicbase.io
                  </a>
                </p>
                <p>
                  If you have questions about this policy or our data practices, contact us at the email above.
                </p>
              </div>

              <div className="legal-section" id="information-we-collect">
                <h2>2. Information we collect</h2>
                <p>We collect the following categories of information:</p>

                <h3>2.1 Account &amp; profile data</h3>
                <ul>
                  <li>Email address</li>
                  <li>Name / display name</li>
                  <li>Username</li>
                  <li>Basic profile settings and preferences</li>
                </ul>
                <p>We use this to create and secure your account, let you sign in, and personalize the Service.</p>

                <h3>2.2 Billing &amp; subscription data</h3>
                <p>
                  When you subscribe to a paid plan, payments are processed by Stripe. Stripe collects and processes
                  your:
                </p>
                <ul>
                  <li>Payment method details (for example, card number and billing address)</li>
                  <li>Billing contact information</li>
                  <li>Subscription status and invoices</li>
                </ul>
                <p>
                  We do not store your full card number or CVV on our servers. We keep basic billing and subscription
                  info (plan, price, status) so we can manage your account and provide receipts.
                </p>

                <h3>2.3 Usage data</h3>
                <p>We may collect information about how you use MusicBase, such as:</p>
                <ul>
                  <li>Log data (IP address, browser type, timestamps)</li>
                  <li>Actions in the app (for example, creating campaigns, adding recipients, viewing stats)</li>
                </ul>
                <p>We use this to keep the Service secure, prevent abuse, and improve product decisions.</p>

                <h3>2.4 Playlist &amp; curator data</h3>
                <p>
                  MusicBase lets you search and organize playlists and curators. For this purpose we store:
                </p>
                <ul>
                  <li>Playlist metadata (name, URL, follower counts if available)</li>
                  <li>Curator contact information (email addresses, Instagram handles, names where available)</li>
                  <li>Your own notes, tags, and campaign assignments</li>
                </ul>
                <p>This data is used only to provide the playlist outreach features of the Service.</p>

                <h3>2.5 Gmail / email data (when you connect your mailbox)</h3>
                <p>
                  If you connect a Google / Gmail account, we use Google OAuth and the Gmail API to access your mailbox
                  on your behalf. With your consent, we may store:
                </p>
                <ul>
                  <li>Your connected mailbox address</li>
                  <li>OAuth tokens (access and refresh tokens) required to send email and read limited mailbox data</li>
                  <li>Metadata about campaign messages (recipient email, subject line, message IDs, timestamps)</li>
                  <li>Basic engagement events (sent, open, click, reply, bounce)</li>
                  <li>Email snippets or limited content used for reply detection</li>
                </ul>
                <p>
                  We do not sell or share the contents of your email messages with third parties for advertising or
                  unrelated purposes.
                </p>
              </div>

              <div className="legal-section" id="gmail-google-data">
                <h2>3. How we use Gmail / Google user data</h2>
                <p>When you connect Gmail, MusicBase uses the following scopes:</p>
                <ul>
                  <li>
                    <code>https://mail.google.com/</code>
                  </li>
                  <li>
                    <code>https://www.googleapis.com/auth/gmail.readonly</code>
                  </li>
                  <li>
                    <code>https://www.googleapis.com/auth/gmail.send</code>
                  </li>
                </ul>

                <p>We use this access strictly to provide campaign features:</p>
                <ul>
                  <li>
                    <strong>Send campaign emails from your own inbox</strong> – Generate and send playlist outreach
                    emails using templates you control and use your connected Gmail address as the &quot;From&quot;
                    address.
                  </li>
                  <li>
                    <strong>Track opens, clicks and bounces for campaign messages</strong> – We embed tracking pixels and
                    tracked links in campaign emails you send and store events (open, click, bounce) tied to your
                    campaign recipients.
                  </li>
                  <li>
                    <strong>Detect replies to your outreach</strong> – We periodically scan recent messages in your inbox
                    (for example, the last few days) to detect replies to your campaign emails. We use message headers,
                    subjects, and snippets to decide whether a message is a reply and to update recipient status (e.g.,
                    &quot;replied&quot;).
                  </li>
                  <li>
                    <strong>Display campaign history inside MusicBase</strong> – For each campaign recipient we show a
                    simple timeline (sent, opened, clicked, replied).
                  </li>
                </ul>

                <p>We do not:</p>
                <ul>
                  <li>Use Gmail data to build advertising profiles.</li>
                  <li>Sell Gmail data or share it with other customers.</li>
                  <li>Read or analyze your email for purposes unrelated to MusicBase features.</li>
                </ul>

                <p>
                  MusicBase&apos;s use and transfer to any other app of information received from Google APIs will adhere
                  to the{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements.
                </p>

                <p>
                  You can revoke MusicBase&apos;s access to your Google account at any time via your Google Account →
                  Security → Third-party access, or by disconnecting your mailbox inside MusicBase.
                </p>
              </div>

              <div className="legal-section" id="legal-bases">
                <h2>4. Legal bases (for users in the EEA/UK)</h2>
                <p>Where applicable law (such as GDPR) requires a legal basis, we rely on:</p>
                <ul>
                  <li>
                    <strong>Contract</strong> – to provide you the Service you signed up for.
                  </li>
                  <li>
                    <strong>Legitimate interests</strong> – to secure and improve the Service, prevent abuse, and measure
                    performance.
                  </li>
                  <li>
                    <strong>Consent</strong> – for connecting your Gmail account and accessing your mailbox data via
                    Google OAuth.
                  </li>
                </ul>
                <p>
                  You can withdraw consent for Gmail access by disconnecting your mailbox in MusicBase and/or revoking
                  access in your Google account settings.
                </p>
              </div>

              <div className="legal-section" id="sharing-info">
                <h2>5. How we share information</h2>
                <p>
                  We share data with third parties only as needed to provide the Service, comply with law, or protect our
                  rights. This includes:
                </p>
                <ul>
                  <li>Infrastructure &amp; hosting providers (e.g. cloud database, servers)</li>
                  <li>Payment processor (Stripe)</li>
                  <li>Email &amp; communication providers (for transactional emails, password resets, etc.)</li>
                </ul>
                <p>
                  These providers act as processors on our behalf and are bound by contractual obligations to handle data
                  securely and only for our specified purposes.
                </p>
                <p>We do not sell your personal information.</p>
              </div>

              <div className="legal-section" id="data-retention">
                <h2>6. Data retention</h2>
                <p>
                  We keep your data for as long as your account is active and as necessary to provide the Service and
                  comply with legal obligations.
                </p>
                <p>Examples:</p>
                <ul>
                  <li>Campaigns, recipients, and stats are stored until you delete them or close your account.</li>
                  <li>
                    Access tokens and mailbox connection details are stored while your mailbox is connected and are
                    deleted or invalidated when you disconnect or revoke access.
                  </li>
                  <li>Billing records may be kept longer for accounting and tax purposes.</li>
                </ul>
                <p>
                  You may request deletion of your account and associated data by contacting us at{" "}
                  <a href="mailto:privacy@musicbase.io">
                    privacy@musicbase.io
                  </a>
                  . Some data (e.g. invoices) may need to be retained where required by law.
                </p>
              </div>

              <div className="legal-section" id="security">
                <h2>7. Security</h2>
                <p>We use reasonable technical and organizational measures to protect your data, including:</p>
                <ul>
                  <li>Encrypted connections (HTTPS)</li>
                  <li>Limited access to production systems</li>
                  <li>Secure storage of authentication tokens and passwords (hashed)</li>
                </ul>
                <p>However, no online system can be 100% secure, and we cannot guarantee absolute security.</p>
              </div>

              <div className="legal-section" id="your-rights">
                <h2>8. Your rights</h2>
                <p>Depending on where you live, you may have rights including:</p>
                <ul>
                  <li>Access the personal data we hold about you</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to or restrict certain processing</li>
                  <li>Data portability</li>
                  <li>Lodge a complaint with a supervisory authority</li>
                </ul>
                <p>
                  You can exercise these rights by emailing{" "}
                  <a href="mailto:privacy@musicbase.io">
                    privacy@musicbase.io
                  </a>
                  . We may need to verify your identity before responding.
                </p>
              </div>

              <div className="legal-section" id="children">
                <h2>9. Children&apos;s privacy</h2>
                <p>
                  MusicBase is not intended for children under 16, and we do not knowingly collect personal information
                  from children. If you believe a child has provided us data, contact us and we will delete it.
                </p>
              </div>

              <div className="legal-section" id="changes">
                <h2>10. Changes to this policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. When we do, we will change the &quot;Last
                  updated&quot; date at the top. If changes are material, we may notify you by email or in-app.
                </p>
                <p>If you continue using MusicBase after changes are posted, you agree to the updated policy.</p>
              </div>

              <p className="legal-small">
                This Privacy Policy does not constitute legal advice. You should consult a lawyer to ensure this document
                fully complies with the laws and regulations applicable to your situation.
              </p>
            </section>

            <footer className="legal-footer">
              <span>© {new Date().getFullYear()} MusicBase</span>
              <span>
                <a href="/">Back to home</a>
              </span>
            </footer>
          </article>
        </main>
      </div>
    </div>
    </>
  );
}
